import { useId } from 'react';
import { Check } from 'lucide-react';

/**
 * One question of the MVP 2 take flow, in each of its three states — the shape
 * the design calls "focused list" (Feature 110, option 2c).
 *
 * The chrome only: the chip, the header, the collapsing panel and the
 * accessibility wiring. What goes inside the panel is the caller's, because
 * readiness questions and Discovery questions answer differently — four fixed
 * tiles with help text against a variable list that is sometimes multi-select.
 *
 * Extracted when Discovery adopted this treatment (Feature 119). Copied instead,
 * the two would have drifted on the first change to either, and the thing being
 * shared is precisely what makes them feel like one flow.
 */

export type RowState = 'open' | 'answered' | 'todo';

/**
 * The chip, which is the only element in a fixed position across all three
 * states — so the filled one moving down the list is what tells you the question
 * changed, without reading a word of it.
 *
 * An answered row carries a check rather than its number, which is what the
 * design asks for and also what the list is for: the numbers say where you are,
 * and the checks say what is behind you. Decorative either way — the row's
 * position is announced by the visually-hidden "Question N of M" beside it.
 */
function StateChip({ position, state }: { position: number; state: RowState }) {
  const style =
    state === 'open'
      ? 'bg-[#026857] text-white'
      : state === 'answered'
        ? 'bg-[#026857]/10 text-[#026857]'
        : 'bg-gray-100 text-gray-400';
  return (
    <span
      aria-hidden
      className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors duration-200 motion-reduce:transition-none ${style}`}
    >
      {state === 'answered' ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : position}
    </span>
  );
}

/**
 * The row's top line, as a button when it reopens the question and a plain
 * element otherwise. One component so the two cannot drift in layout.
 */
function Header({
  as,
  onClick,
  disabled,
  label,
  children,
}: {
  as: 'button' | 'div';
  onClick: () => void;
  disabled: boolean;
  label: string;
  children: React.ReactNode;
}) {
  const layout = 'flex w-full items-center gap-3';
  if (as === 'div') return <div className={layout}>{children}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`${layout} rounded-lg text-left disabled:opacity-60`}
    >
      {children}
    </button>
  );
}

export function FocusRow({
  question,
  position,
  total,
  state,
  /** What the client chose, as the collapsed row shows it — may be abbreviated. */
  answerSummary,
  /**
   * The same answer in full, for the accessible name. Defaults to what is shown.
   *
   * They come apart when a multi-select is abbreviated to "First choice +2": the
   * badge has a row to fit into, and a screen-reader user moving down collapsed
   * rows has nothing but this label to tell them what they said.
   */
  answerSpoken,
  isBusy,
  onReopen,
  rowRef,
  children,
}: {
  question: string;
  position: number;
  total: number;
  state: RowState;
  answerSummary: string | undefined;
  answerSpoken?: string;
  isBusy: boolean;
  onReopen: () => void;
  rowRef: React.RefObject<HTMLDivElement | null> | undefined;
  children: React.ReactNode;
}) {
  const open = state === 'open';
  const rowId = useId();

  return (
    <div
      ref={rowRef}
      {...(open
        ? {
            role: 'group',
            // Named by its own eyebrow and question text rather than by an
            // `aria-label`: focus is moved here the moment a row opens, and a
            // label would override the descendants — announcing "Question 2 of
            // 5" and never the question that just appeared.
            'aria-labelledby': `${rowId}-position ${rowId}-question`,
          }
        : {})}
      // Focusable only programmatically: when a row closes, the control just
      // pressed goes with it, so focus is moved here rather than lost.
      tabIndex={open ? -1 : undefined}
      className={`rounded-[14px] border outline-none transition-colors duration-200 motion-reduce:transition-none ${
        open
          ? 'border-[#026857]/25 bg-white p-[22px] shadow-sm'
          : state === 'answered'
            ? // Hover on the row, not the button inside it, so the whole card
              // lights up and reads as the target it now is.
              'border-gray-200 bg-white px-4 py-3 hover:border-[#026857]/40'
            : 'border-gray-100 px-4 py-3'
      }`}
    >
      {/*
        An answered row is one target, not a small link inside a dead card: the
        whole header reopens it. "Change" is then the label for what the click
        does rather than a control of its own — a second button inside this one
        would not be valid markup, and the word alone is a poor hit area.
      */}
      <Header
        as={answerSummary && !open ? 'button' : 'div'}
        onClick={onReopen}
        disabled={isBusy}
        /*
         * The question and the answer, not just the position.
         *
         * This said "Change answer for question 3" and nothing else, on the
         * reasoning that the full text would be a long and unstable name. That
         * was the wrong trade: a screen-reader user moving down five collapsed
         * rows heard five near-identical labels and had to open each one to find
         * out which question it was and what they had said. Long and informative
         * beats short and useless.
         */
        label={`Change answer for question ${position} of ${total}: ${question} — answered ${answerSpoken ?? answerSummary}`}
      >
        <StateChip position={position} state={state} />
        {/* The chip is decorative, so the position is said here instead — it is
            half of the open row's accessible name. */}
        <span id={`${rowId}-position`} className="sr-only">
          Question {position} of {total}
        </span>
        <span
          id={`${rowId}-question`}
          className={`min-w-0 flex-1 text-left ${
            open
              ? 'text-[16.5px] leading-relaxed text-gray-900'
              : `block truncate text-sm ${state === 'answered' ? 'text-gray-600' : 'text-gray-400'}`
          }`}
          // Closed rows ellipsize, so the full wording is kept reachable.
          title={open ? undefined : question}
        >
          {question}
        </span>
        {!open && answerSummary && (
          <>
            {/*
              Capped and allowed to shrink. Held at its natural width it ate the
              row: the readiness answers are four short words, but a Discovery
              option can be a sentence, and the question it belongs to was
              squeezed down to "Who …" while Change spilled past the border.
              The question is what identifies the row, so it keeps the space.
            */}
            <span
              title={answerSummary}
              className="min-w-0 max-w-[40%] shrink truncate rounded-full bg-[#026857]/10 px-2.5 py-0.5 text-xs font-semibold text-[#026857]"
            >
              {answerSummary}
            </span>
            <span className="shrink-0 text-[12.5px] font-semibold text-[#026857]">Change</span>
          </>
        )}
      </Header>

      {/*
        Height animated through grid-template-rows rather than height: the
        contents stay mounted, and the browser is not animating a layout property
        frame by frame.
      */}
      <div
        // Hidden from assistive tech while closed, not just from the tab order:
        // the contents stay mounted so the row can animate, and every option of
        // every question read aloud is the noise this layout exists to remove.
        aria-hidden={!open}
        className="grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none"
        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  );
}
